import type { ReactNode } from 'react';
import { useAuth } from '../context/auth-context';
import { getCountryCode, COUNTRIES } from '../constants/countries';

// Componentes de Managers por país - Argentina
import { AddManagerFormAR } from '../components/form/managers/AR/add-manager-form-ar';
import { EditManagerFormAR } from '../components/form/managers/AR/edit-manager-form-ar';

// Componentes de Managers por país - República Dominicana
import { AddManagerFormDO } from '../components/form/managers/DO/add-manager-form-do';
import { EditManagerFormDO } from '../components/form/managers/DO/edit-manager-form-do';

// Componentes de Branches por país - Argentina
import { BranchInfoStepAR } from '../components/form/first-login/add-branch/AR/branch-info-step-ar';

// Componentes de Branches por país - República Dominicana
import { BranchInfoStepDO } from '../components/form/first-login/add-branch/DO/branch-info-step-do';

// Componentes de Employee Details por país - Argentina
import { EmployeeDetailsAR } from '../components/teams/AR/employee-details-ar';

// Componentes de Employee Details por país - República Dominicana
import { EmployeeDetailsDO } from '../components/teams/DO/employee-details-do';

// Componentes de Settings por país - Argentina
import { FormSettingsAR } from '../components/settings/AR/form-settings-ar';

// Componentes de Settings por país - República Dominicana
import { FormSettingsDO } from '../components/settings/DO/form-settings-do';

type Component = {
  page?: ReactNode;
};

interface ComponentByCountry {
  [country: string]: {
    [componentName: string]: Component;
  };
}

/**
 * Tipos de páginas/componentes disponibles para renderizar por país
 * Agregar aquí cuando se necesite un nuevo componente específico por país
 */
type FlowPageMap =
  | 'addManagerForm'
  | 'editManagerForm'
  | 'branchInfoStep'
  | 'employeeDetails'
  | 'formSettings';
  // Agregar más componentes según se necesite

interface FlowPagesProps {
  page: FlowPageMap;
  props?: any; // Props que se pasarán al componente renderizado
}

/**
 * Hook para obtener componentes específicos según el país del usuario
 * 
 * @example
 * ```tsx
 * const Component = useFlowPages({ 
 *   page: 'addManagerForm', 
 *   props: { setIsModalOpen, fetchManagers } 
 * });
 * return <>{Component}</>;
 * ```
 */
export function useFlowPages({ page, props }: FlowPagesProps): ReactNode {
  const { country } = useAuth();
  const countryCode = getCountryCode(country);

  /**
   * Diccionario de componentes organizados por país
   * Usa los mismos códigos que el backend (AR, DO, etc.)
   */
  const componentsByCountry: ComponentByCountry = {
    [COUNTRIES.AR]: {
      addManagerForm: { page: <AddManagerFormAR {...props} /> },
      editManagerForm: { page: <EditManagerFormAR {...props} /> },
      branchInfoStep: { page: <BranchInfoStepAR {...props} /> },
      employeeDetails: { page: <EmployeeDetailsAR {...props} /> },
      formSettings: { page: <FormSettingsAR {...props} /> },
    },
    [COUNTRIES.DO]: {
      addManagerForm: { page: <AddManagerFormDO {...props} /> },
      editManagerForm: { page: <EditManagerFormDO {...props} /> },
      branchInfoStep: { page: <BranchInfoStepDO {...props} /> },
      employeeDetails: { page: <EmployeeDetailsDO {...props} /> },
      formSettings: { page: <FormSettingsDO {...props} /> },
    },
  };

  const hasComponent = componentsByCountry[countryCode];

  /**
   * Obtiene el componente específico para el país actual
   * Si no existe, renderiza un fallback
   */
  const getComponent = (componentName: string): ReactNode => {
    if (hasComponent && componentsByCountry[countryCode][componentName]) {
      return componentsByCountry[countryCode][componentName].page;
    }

    // Fallback: componente no encontrado para el país
    return (
      <div className="p-4 text-center text-red-500">
        <p>Componente no disponible para este país</p>
        <p className="text-sm text-gray-500">País: {countryCode} | Componente: {componentName}</p>
      </div>
    );
  };

  return getComponent(page);
}

